import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_PAGE_SIZE = 20;
const LOAD_MORE_INDICATOR_DELAY_MS = 400;

export const useSidebarInfiniteScroll = <T,>(
  filteredItems: T[],
  pageSize = DEFAULT_PAGE_SIZE,
  resetKey = ""
) => {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [listElement, setListElement] = useState<HTMLUListElement | null>(null);
  const [sentinelElement, setSentinelElement] = useState<HTMLLIElement | null>(
    null
  );
  const loadMoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const totalCount = filteredItems.length;
  const hasMore = visibleCount < totalCount;

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [resetKey, pageSize]);

  useEffect(
    () => () => {
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }
    },
    []
  );

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);

    loadMoreTimeoutRef.current = setTimeout(() => {
      setVisibleCount((currentCount) =>
        Math.min(currentCount + pageSize, totalCount)
      );
      setIsLoadingMore(false);
    }, LOAD_MORE_INDICATOR_DELAY_MS);
  }, [hasMore, isLoadingMore, pageSize, totalCount]);

  const listRef = useCallback((node: HTMLUListElement | null) => {
    setListElement(node);
  }, []);

  const sentinelRef = useCallback((node: HTMLLIElement | null) => {
    setSentinelElement(node);
  }, []);

  useEffect(() => {
    if (!listElement || !sentinelElement || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      {
        root: listElement,
        threshold: 0.1,
      }
    );

    observer.observe(sentinelElement);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, listElement, loadMore, sentinelElement]);

  const displayedItems = useMemo(
    () => filteredItems.slice(0, visibleCount),
    [filteredItems, visibleCount]
  );

  return {
    displayedItems,
    totalCount,
    hasMore,
    isLoadingMore,
    sentinelRef,
    listRef,
  };
};
